import type _jquery from "jquery";
import { reactive, ref, type InjectionKey, type Ref, provide } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
} from "@mcwd/util-vue";

import { useMarketoInputProxy } from "./use-marketo-input-proxy/index.js";
import { MarketoAllInputMeta } from "../types/definitions/inputs/marketo-input-meta.js";
import { type MarketoVals, HiddenMarketoInputName } from '../types/definitions/inputs/marketo-input-names-and-values.js';
import { useFormLogger } from "./use-form-logger.js";

type MarketoHiddenInputValues<EnabledHiddenField extends HiddenMarketoInputName> = {
  [Key in EnabledHiddenField]: Required<MarketoVals>[Key]
};
type MarketoHiddenFieldRefValues<EnabledHiddenField extends HiddenMarketoInputName> = {
  [Key in EnabledHiddenField]: Ref<Required<MarketoVals>[Key]>
};

type UseMarketoHiddenInputsInjectionValues<
  EnabledHiddenField extends HiddenMarketoInputName = HiddenMarketoInputName
> = InjectionValuesRecord<{
  MarketoHiddenInputValues: MarketoHiddenInputValues<EnabledHiddenField>
}>;

type UseMarketoHiddenInputsInjectionKeys<
  EnabledHiddenField extends HiddenMarketoInputName = HiddenMarketoInputName
> = InjectionKeysRecord<UseMarketoHiddenInputsInjectionValues<EnabledHiddenField>>;

const UseMarketoHiddenInputsInjectionKeys = CreateInjectionKeysRecord<UseMarketoHiddenInputsInjectionKeys>({
  MarketoHiddenInputValues: "MarketoHiddenInputValues",
});


export function initMarketoHiddenInputs<EnabledHiddenField extends HiddenMarketoInputName>(enabledHiddenFields: readonly EnabledHiddenField[]) {
  if (checkValuesAlreadyProvided(UseMarketoHiddenInputsInjectionKeys)) {
    return;
  }

  const HiddenInputsMeta = Object.fromEntries(
    enabledHiddenFields.map((field) => [field, MarketoAllInputMeta[field]])
  ) as Pick<MarketoAllInputMeta, EnabledHiddenField>;

  const marketoHiddenInputValues = reactive(
    enabledHiddenFields.reduce(
      (result: Record<EnabledHiddenField, Ref<any>>, currentKey: EnabledHiddenField) => {
        const proxyModelValue = ref<MarketoHiddenInputValues<EnabledHiddenField>[typeof currentKey]>(null);
        useMarketoInputProxy({
          inputName: HiddenInputsMeta[currentKey].marketoName,
          proxyInputValue: proxyModelValue,
          callerLabel: "initMarketoHiddenInputs"
        });
        result[currentKey] = proxyModelValue;
        return result;
      },
      {} as Record<EnabledHiddenField, any>
    ) as MarketoHiddenFieldRefValues<EnabledHiddenField>
  ) as MarketoHiddenInputValues<EnabledHiddenField>;

  provide(
    UseMarketoHiddenInputsInjectionKeys.MarketoHiddenInputValues as
      UseMarketoHiddenInputsInjectionKeys<EnabledHiddenField>["MarketoHiddenInputValues"],
    marketoHiddenInputValues
  );
}

export function useMarketoHiddenInputs<EnabledHiddenField extends HiddenMarketoInputName> ({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger({ callerLabel: "useMarketoHiddenInputs"});
  try {
    const marketoHiddenInputValues = injectRequiredWithSelf(UseMarketoHiddenInputsInjectionKeys.MarketoHiddenInputValues as InjectionKey<MarketoHiddenInputValues<EnabledHiddenField>>);
    return { marketoHiddenInputValues };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}